<template>
  <div class="analytics-view view">
    <!-- In-view permission guard - the router has no permission middleware.
         Same pattern as DrugRegister.vue; Kraken 403s regardless. -->
    <div v-if="!canView" class="panel analytics-panel">
      <h3 style="margin-top: 4em">You do not have permission to view analytics.</h3>
    </div>

    <div v-else class="panel analytics-panel">
      <div class="analytics-header">
        <div class="analytics-tabs">
          <h3 class="tab" @click="$router.push('/analytics/governance')">Governance</h3>
          <h3 class="tab selected-tab">Theatre &amp; Ward</h3>
        </div>
        <div class="analytics-filters">
          <Calendar v-model="dateRange" selectionMode="range" placeholder="Date range..." dateFormat="dd/mm/yy" />
          <button class="button submit-button" @click="fetchAll()">Apply</button>
        </div>
      </div>

      <div class="tile-row" v-if="summary">
        <HeroFigure v-bind="summary.hero" :spark="summary.hero.spark" downIsGood />
        <StatTile
          v-for="tile in summary.tiles"
          :key="tile.key"
          v-bind="tile"
          downIsGood
        />
      </div>
      <div class="tile-row-placeholder" v-else-if="loading.summary">
        <i class="pi pi-spin pi-spinner"></i>
      </div>

      <div class="chart-section">
        <h4 class="chart-section-label">Open now</h4>
        <div class="chart-grid">
          <ChartCard
            title="Open checkouts"
            subtitle="Hours out, longest first — amber past 8h, red past 24h"
            type="bar"
            height="320"
            :options="openCheckoutOptions"
            :categories="openCheckoutCategories"
            :series="openCheckoutSeries"
            :loading="loading.open"
          />
          <ChartCard
            title="Requisition cycle stage medians"
            subtitle="Median hours in each stage"
            type="bar"
            :options="cycleOptions"
            :categories="panels.cycle.categories"
            :series="panels.cycle.series"
            :loading="loading.cycle"
          />
        </div>
      </div>

      <div class="chart-section">
        <h4 class="chart-section-label">Flow and wastage</h4>
        <div class="chart-grid">
          <ChartCard
            title="Checkout activity by weekday and hour"
            subtitle="Average checkouts per week"
            type="heatmap"
            height="300"
            :options="heatmapChartOptions"
            :categories="panels.heatmap.categories"
            :series="heatmapSeries"
            :loading="loading.heatmap"
          />
          <ChartCard
            title="Administered vs discarded"
            subtitle="Units, worst discard share first"
            type="bar"
            :options="wastageOptions"
            :categories="wastageCategories"
            :series="panels.wastage.series"
            :loading="loading.wastage"
          />
          <ChartCard
            title="Par levels: on hand vs minimum and par"
            subtitle="Units held per item, 15 most depleted"
            type="bar"
            :height="parChartHeight"
            :options="parOptions"
            :categories="panels.par.categories"
            :series="panels.par.series"
            :loading="loading.par"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import HeroFigure from '@/components/analytics/HeroFigure.vue';
import StatTile from '@/components/analytics/StatTile.vue';
import ChartCard from '@/components/analytics/ChartCard.vue';
import {
  bandedBarOptions,
  barOptions,
  endLabels,
  heatmapOptions,
  ordinalBarOptions,
  withoutLegend,
  STATUS,
} from '@/services/chartTheme';
import { defaultRange, loadSequentially } from '@/services/analyticsApi';
import '@/assets/css/analytics.scss';

/** Anything out longer than this is overdue back to the safe. */
const OVERDUE_HOURS = 24;
/** Long enough to be worth chasing, not yet overdue. */
const ATTENTION_HOURS = 8;

export default {
  name: 'TheatreDashboard',
  components: { HeroFigure, StatTile, ChartCard },
  data() {
    return {
      dateRange: defaultRange(),
      runId: 0,
      loading: {
        summary: false,
        open: false,
        cycle: false,
        heatmap: false,
        wastage: false,
        par: false,
      },
      summary: null,
      openCheckouts: [],
      // Ticks each minute so the open-checkout bars advance live - the
      // endpoint deliberately ships out_at, not a frozen elapsed figure.
      now: Date.now(),
      ticker: null,
      panels: {
        cycle: { categories: [], series: [] },
        heatmap: { categories: [], series: [] },
        wastage: { categories: [], series: [] },
        par: { categories: [], series: [] },
      },
    };
  },
  computed: {
    canView() {
      return (
        this.$store.state.user.analytics_active &&
        this.$store.state.user.role.permissions.includes('can_view_transactions_report')
      );
    },
    /**
     * Elapsed hours per open checkout, longest first.
     *
     * This was a datetime timeline, and on a timeline every bar is drawn
     * against the same absolute axis: one checkout left open for eight weeks
     * set the scale and the other seven rendered as slivers. What the panel is
     * actually asked is "how long has this been out", so hours out is the
     * measure and the bars are comparable again.
     */
    sortedCheckouts() {
      return this.openCheckouts
        .map((row) => ({
          row,
          // Kraken runs on UTC and ships out_at as a bare 'Y-m-d H:i:s' string. Without the Z
          // the browser reads it as local time, which in AEST added ten hours to every elapsed
          // figure and pushed checkouts into the overdue band hours early.
          hours: Math.max(0, (this.now - new Date(`${row.out_at.replace(' ', 'T')}Z`).getTime()) / 3600000),
        }))
        .sort((a, b) => b.hours - a.hours);
    },
    openCheckoutSeries() {
      if (!this.sortedCheckouts.length) return [];
      return [{ name: 'Hours out', data: this.sortedCheckouts.map((c) => Number(c.hours.toFixed(1))) }];
    },
    /**
     * Two lines per row. A single-line label was truncated mid-word by the
     * axis ("Ward 1 (2 items, clinician b..."), and with the parenthetical cut
     * off two different checkouts from the same theatre read identically.
     */
    openCheckoutCategories() {
      return this.sortedCheckouts.map(({ row }) => [
        row.location,
        `${row.items} item${row.items === 1 ? '' : 's'}${row.type === 'clinician_hold' ? ' · clinician bag' : ''}`,
      ]);
    },
    openCheckoutOptions() {
      // Elapsed time here means a state, not an identity, so the fill takes
      // status tokens rather than series slots - and the hours printed at each
      // tip are what keep a light amber bar readable.
      const options = withoutLegend(
        bandedBarOptions([
          { upTo: ATTENTION_HOURS, color: STATUS.good },
          { upTo: OVERDUE_HOURS, color: STATUS.warning },
          { upTo: null, color: STATUS.critical },
        ])
      );

      return {
        ...options,
        ...endLabels({ horizontal: true, formatter: (v) => `${v}h` }),
        grid: { ...options.grid, padding: { ...options.grid.padding, right: 52 } },
        tooltip: { ...options.tooltip, y: { formatter: (v) => `${v} hours out` } },
      };
    },
    cycleOptions() {
      // Four ordered stages: ordinal, so the ramp shows the sequence, and the
      // medians ride on the caps instead of a y-axis.
      return { ...ordinalBarOptions(), ...endLabels({ formatter: (v) => `${v}h` }) };
    },
    /**
     * The discard share moves into the axis label rather than onto the bar. It
     * is what the panel sorts by, so it has to be visible - and an interior
     * stacked segment has no free end to hang a label off, while a label set
     * inside the orange fill would sit at 3:1 against white text.
     */
    wastageCategories() {
      const [administered, discarded] = this.panels.wastage.series;
      if (!discarded) return this.panels.wastage.categories;

      return this.panels.wastage.categories.map((category, i) => {
        const out = (administered.data[i] || 0) + (discarded.data[i] || 0);
        if (!out) return [category];
        return [category, `${Math.round((discarded.data[i] / out) * 100)}% discarded`];
      });
    },
    wastageOptions() {
      return barOptions({ stacked: true, horizontal: true });
    },
    /**
     * Rows keep a constant thickness whatever the panel returns, rather than
     * compressing into each other inside a fixed box. Capped at the 15 rows the
     * endpoint sends, so a larger response cannot stretch the panel off screen.
     */
    parChartHeight() {
      const rows = Math.min(this.panels.par.categories.length, 15);
      return Math.max(280, rows * 34 + 80);
    },
    parOptions() {
      const options = barOptions({ horizontal: true });

      return {
        ...options,
        yaxis: {
          ...options.yaxis,
          labels: {
            ...options.yaxis.labels,
            // Categories here are "drug - location", long enough to eat the
            // plot area. Cap the gutter and trim the text; the tooltip still
            // carries the whole name.
            maxWidth: 220,
            formatter: (value) =>
              (typeof value === 'string' && value.length > 34 ? `${value.slice(0, 33)}\u2026` : value),
          },
        },
      };
    },
    /**
     * Apex draws series[0] along the bottom, which left Monday under Sunday.
     * Reversed so the week reads downwards.
     */
    heatmapSeries() {
      return this.panels.heatmap.series.slice().reverse();
    },
    heatmapChartOptions() {
      const max = Math.max(
        1,
        ...this.panels.heatmap.series.flatMap((s) => s.data.filter((v) => typeof v === 'number'))
      );
      return heatmapOptions(max);
    },
  },
  methods: {
    fetchAll() {
      if (!this.dateRange || !this.dateRange[0]) return;
      const run = ++this.runId;

      // Sequential with the timeline first (it is the panel a theatre manager
      // acts on) and theatre-summary last - its multiple internal aggregates
      // make it the slowest of the six. See loadSequentially's doc comment.
      loadSequentially(
        [
          { key: 'open', endpoint: '/analytics/open-checkouts', assign: (d) => (this.openCheckouts = d.rows || []) },
          { key: 'cycle', endpoint: '/analytics/requisition-cycle', assign: (d) => (this.panels.cycle = d) },
          { key: 'heatmap', endpoint: '/analytics/theatre-heatmap', assign: (d) => (this.panels.heatmap = d) },
          { key: 'wastage', endpoint: '/analytics/wastage', assign: (d) => (this.panels.wastage = d) },
          { key: 'par', endpoint: '/analytics/par-levels', assign: (d) => (this.panels.par = d) },
          { key: 'summary', endpoint: '/analytics/theatre-summary', assign: (d) => (this.summary = d) },
        ],
        this.dateRange,
        this.$store.state.header,
        this.loading,
        () => run !== this.runId
      );
    },
  },
  mounted() {
    if (this.canView) {
      this.fetchAll();
      this.ticker = setInterval(() => {
        this.now = Date.now();
      }, 60000);
    }
  },
  beforeUnmount() {
    if (this.ticker) clearInterval(this.ticker);
  },
};
</script>
